/**
 * Metro 2 Account Card Component
 * 
 * Shows comprehensive account information with:
 * - Bureau coverage and match status
 * - Field logic status
 * - Monthly payment history status
 * - Collection/Charge-off/Student loan specific review
 * - Rule drawer integration
 * - Eligibility badge and confidence score
 */

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Check,
  AlertTriangle,
  Info,
  Eye,
  FileText,
  Building2,
  Calendar,
  DollarSign,
  Activity,
  Shield,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Scale,
  FileCode,
  BookOpen,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Eligibility status configurations
const ELIGIBILITY_CONFIG = {
  confirmed_metro2: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-700',
    label: 'AI Recommended for Metro 2',
    icon: CheckCircle,
    description: 'Confirmed Metro 2 technical conflict detected',
  },
  needs_metro2_review: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
    label: 'Metro 2 Review Needed',
    icon: AlertTriangle,
    description: 'Bureau differences require technical review',
  },
  metro2_eligible: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-700',
    label: 'Metro 2 Eligible',
    icon: Check,
    description: 'Eligible for Metro 2 technical dispute',
  },
  factual_eligible: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
    label: 'Better for Factual Dispute',
    icon: FileText,
    description: 'Account is disputable but lacks Metro 2 technical conflicts',
  },
  evidence_needed: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-300 dark:border-purple-700',
    label: 'Evidence Needed',
    icon: FileText,
    description: 'Supporting documentation required before dispute',
  },
  inquiry_flow_required: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-300',
    border: 'border-cyan-300 dark:border-cyan-700',
    label: 'Inquiry Flow Required',
    icon: Eye,
    description: 'Use dedicated inquiry dispute flow',
  },
  personal_info_flow_required: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-700 dark:text-indigo-300',
    border: 'border-indigo-300 dark:border-indigo-700',
    label: 'Personal Info Flow Required',
    icon: Shield,
    description: 'Use personal information dispute flow',
  },
  normal_variation: {
    bg: 'bg-stone-100 dark:bg-stone-800',
    text: 'text-stone-600 dark:text-stone-400',
    border: 'border-stone-300 dark:border-stone-600',
    label: 'Normal Variation',
    icon: Check,
    description: 'Normal bureau reporting variation',
  },
  not_recommended: {
    bg: 'bg-stone-100 dark:bg-stone-800',
    text: 'text-stone-500 dark:text-stone-500',
    border: 'border-stone-300 dark:border-stone-600',
    label: 'Not Recommended',
    icon: XCircle,
    description: 'No clear dispute path identified',
  },
};

// Bureau match status configurations
const BUREAU_MATCH_STATUS = {
  all_match: {
    label: 'All Bureaus Match',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: CheckCircle,
  },
  mismatch: {
    label: 'Bureau Mismatch',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    icon: AlertTriangle,
  },
  partial_data: {
    label: 'Partial Data',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    icon: Info,
  },
  missing_data: {
    label: 'Missing Data',
    className: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400',
    icon: AlertCircle,
  },
};

// Field logic status configurations
const FIELD_LOGIC_STATUS = {
  clean: {
    label: 'Clean',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: CheckCircle,
  },
  review_needed: {
    label: 'Review Needed',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    icon: AlertTriangle,
  },
  confirmed_conflict: {
    label: 'Confirmed Conflict',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    icon: XCircle,
  },
  evidence_needed: {
    label: 'Evidence Needed',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    icon: FileText,
  },
};

// Bureau badge colors
const BUREAU_COLORS = {
  transunion: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  experian: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  equifax: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
};

// Rule Summary Drawer Component
const RuleSummaryDrawer = ({ ruleKey, open, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [ruleSummary, setRuleSummary] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchRuleSummary = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/metro2-intelligence/rules/${ruleKey}/summary`);
      setRuleSummary(response.data.summary);
    } catch (err) {
      setError('Failed to load rule details');
      console.error('Rule summary fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [ruleKey]);

  React.useEffect(() => {
    if (open && ruleKey) {
      fetchRuleSummary();
    }
  }, [open, ruleKey, fetchRuleSummary]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[450px] bg-white dark:bg-stone-950 border-stone-200 dark:border-stone-800 overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-stone-900 dark:text-white">Rule Details</SheetTitle>
          <SheetDescription className="font-mono text-stone-500">{ruleKey}</SheetDescription>
        </SheetHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : ruleSummary ? (
          <div className="space-y-6 mt-6">
            {/* Rule Name */}
            <div>
              <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Rule Name</h4>
              <p className="text-lg font-semibold text-stone-900 dark:text-white">{ruleSummary.rule_name}</p>
            </div>

            {/* Why Flagged */}
            {ruleSummary.description && (
              <div>
                <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Why Flagged</h4>
                <p className="text-sm text-stone-700 dark:text-stone-300">{ruleSummary.description}</p>
              </div>
            )}

            {/* Metro 2 Field Codes */}
            {ruleSummary.metro2_codes?.length > 0 && (
              <div>
                <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Metro 2 Field Codes</h4>
                <div className="flex flex-wrap gap-2">
                  {ruleSummary.metro2_codes.map((code, i) => (
                    <Badge key={i} variant="outline" className="bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                      {code}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Legal Authorities */}
            {ruleSummary.legal_basis_mapping?.length > 0 && (
              <div>
                <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Legal Authorities</h4>
                <div className="space-y-1">
                  {ruleSummary.legal_basis_mapping.map((law, i) => (
                    <p key={i} className="text-sm text-blue-600 dark:text-blue-400 font-mono">{law}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Eligibility & Route */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Eligibility</h4>
                <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  {ruleSummary.eligibility_status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div>
                <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Route</h4>
                <Badge variant="outline" className="bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300">
                  {ruleSummary.recommended_route?.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>

            {/* Letter Tone */}
            <div>
              <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Letter Tone</h4>
              <Badge variant="outline" className="bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300">
                {ruleSummary.letter_tone?.replace(/_/g, ' ')}
              </Badge>
            </div>

            {/* Consumer Explanation */}
            {ruleSummary.consumer_friendly_explanation && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
                <h4 className="text-sm text-emerald-700 dark:text-emerald-400 uppercase mb-2">Consumer Summary</h4>
                <p className="text-sm text-emerald-800 dark:text-emerald-300">{ruleSummary.consumer_friendly_explanation}</p>
              </div>
            )}

            {/* Evidence Requirements */}
            {ruleSummary.evidence_requirements?.length > 0 && (
              <div>
                <h4 className="text-sm text-stone-500 dark:text-stone-400 uppercase mb-2">Evidence May Help</h4>
                <ul className="space-y-1">
                  {ruleSummary.evidence_requirements.map((ev, i) => (
                    <li key={i} className="text-sm text-stone-600 dark:text-stone-400 flex items-center gap-2">
                      <FileText size={14} className="text-purple-500" />
                      {ev}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

// Main Account Card Component
const Metro2AccountCard = ({
  account,
  selected,
  onSelect,
  onViewDetails,
  showCheckbox = true,
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [ruleDrawerOpen, setRuleDrawerOpen] = useState(false);
  const [selectedRuleKey, setSelectedRuleKey] = useState(null);

  // Determine eligibility config
  const eligibilityStatus = account.eligibility_status || account.classification || 'not_recommended';
  const eligibilityConfig = ELIGIBILITY_CONFIG[eligibilityStatus] || ELIGIBILITY_CONFIG.not_recommended;
  const EligibilityIcon = eligibilityConfig.icon;

  // Bureau match status
  const bureauMatchStatus = account.bureau_match_status || 'missing_data';
  const bureauMatchConfig = BUREAU_MATCH_STATUS[bureauMatchStatus] || BUREAU_MATCH_STATUS.missing_data;
  const BureauMatchIcon = bureauMatchConfig.icon;

  // Field logic status
  const fieldLogicStatus = account.field_logic_status || 'review_needed';
  const fieldLogicConfig = FIELD_LOGIC_STATUS[fieldLogicStatus] || FIELD_LOGIC_STATUS.review_needed;
  const FieldLogicIcon = fieldLogicConfig.icon;

  // Extract account info
  const creditorName = account.creditor_name || account.creditorName || 'Unknown Creditor';
  const accountNumber = account.account_number_masked || account.accountNumberMasked || '****';
  const accountType = account.account_type || account.accountType || 'Unknown';
  const accountTypeDetail = account.account_type_detail || account.accountTypeDetail || '';
  const accountStatus = account.account_status || account.accountStatus || '';
  const paymentStatus = account.payment_status || account.paymentStatus || '';
  const balance = account.balance || 0;
  const pastDue = account.past_due || account.pastDue || 0;
  const bureaus = account.bureaus || account.bureaus_reported || [];
  const confidenceScore = account.confidence_score || account.confidenceScore || 0;

  // Flags
  const isCollection = account.is_collection || account.isCollection || false;
  const isChargeOff = account.is_chargeoff || account.isChargeOff || account.is_charge_off || false;
  const isStudentLoan = account.is_student_loan || account.isStudentLoan || false;
  const isNegative = account.is_negative || account.isNegative || false;
  const evidenceNeeded = account.evidence_needed || account.evidenceNeeded || false;

  // Matched rules
  const matchedRules = account.matched_rules || [];
  const matchedRedFlags = account.matched_red_flags || [];
  const issueCategories = account.issue_categories || account.detected_issues || [];

  // Field breakdown
  const fieldBreakdown = account.field_level_breakdown || account.fieldBreakdown || [];

  // Handle rule click
  const handleViewRule = (ruleKey) => {
    setSelectedRuleKey(ruleKey);
    setRuleDrawerOpen(true);
  };

  // Format currency
  const formatCurrency = (val) => {
    if (typeof val === 'string') {
      return val;
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  };

  return (
    <>
      <div
        className={`border rounded-xl overflow-hidden transition-all ${
          selected
            ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-600'
            : 'border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900'
        } ${compact ? '' : 'hover:shadow-md'}`}
        data-testid={`metro2-account-card-${account.item_id || account.tradeline_id}`}
      >
        {/* Card Header */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            {showCheckbox && (
              <div className="pt-1">
                <Checkbox
                  checked={selected}
                  onCheckedChange={onSelect}
                  className="border-stone-300 dark:border-stone-600"
                  data-testid="account-card-checkbox"
                />
              </div>
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Top Row: Creditor & Badges */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <h4 className="font-semibold text-stone-900 dark:text-white truncate">{creditorName}</h4>
                  <p className="text-sm text-stone-500 dark:text-stone-400">
                    {accountNumber} • {accountType}
                    {accountTypeDetail && ` (${accountTypeDetail})`}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Eligibility Badge */}
                  <Badge
                    variant="outline"
                    className={`${eligibilityConfig.bg} ${eligibilityConfig.text} ${eligibilityConfig.border} text-xs`}
                  >
                    <EligibilityIcon size={12} className="mr-1" />
                    {eligibilityConfig.label}
                  </Badge>
                  {/* Confidence Score */}
                  {confidenceScore > 0 && (
                    <Badge variant="outline" className="text-xs bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400">
                      {Math.round(confidenceScore * 100)}%
                    </Badge>
                  )}
                </div>
              </div>

              {/* Bureau Coverage */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {bureaus.map((bureau, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className={`text-xs ${BUREAU_COLORS[bureau.toLowerCase()] || 'bg-stone-100 text-stone-600'}`}
                  >
                    {bureau}
                  </Badge>
                ))}
                {isNegative && (
                  <Badge variant="outline" className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    Negative
                  </Badge>
                )}
                {isCollection && (
                  <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                    Collection
                  </Badge>
                )}
                {isChargeOff && (
                  <Badge variant="outline" className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    Charge-Off
                  </Badge>
                )}
                {isStudentLoan && (
                  <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    Student Loan
                  </Badge>
                )}
                {evidenceNeeded && (
                  <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                    Evidence Needed
                  </Badge>
                )}
              </div>

              {/* Status Row */}
              <div className="flex items-center gap-3 flex-wrap text-xs">
                {/* Bureau Match Status */}
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${bureauMatchConfig.className}`}>
                  <BureauMatchIcon size={12} />
                  <span>{bureauMatchConfig.label}</span>
                </div>
                {/* Field Logic Status */}
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${fieldLogicConfig.className}`}>
                  <FieldLogicIcon size={12} />
                  <span>{fieldLogicConfig.label}</span>
                </div>
                {/* Balance */}
                {balance > 0 && (
                  <div className="flex items-center gap-1 text-stone-600 dark:text-stone-400">
                    <DollarSign size={12} />
                    <span>Balance: {formatCurrency(balance)}</span>
                  </div>
                )}
                {/* Past Due */}
                {pastDue > 0 && (
                  <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                    <AlertCircle size={12} />
                    <span>Past Due: {formatCurrency(pastDue)}</span>
                  </div>
                )}
              </div>

              {/* Issue Categories */}
              {issueCategories.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {issueCategories.slice(0, 4).map((issue, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700"
                    >
                      {typeof issue === 'string' ? issue.replace(/_/g, ' ') : issue.label || issue.category}
                    </Badge>
                  ))}
                  {issueCategories.length > 4 && (
                    <Badge variant="outline" className="text-xs bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400">
                      +{issueCategories.length - 4} more
                    </Badge>
                  )}
                </div>
              )}

              {/* Expand/Collapse Button */}
              {!compact && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-3 flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {expanded ? 'Hide Details' : 'View Details'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && !compact && (
          <div className="border-t border-stone-200 dark:border-stone-700 p-4 bg-stone-50 dark:bg-stone-900/50 space-y-4">
            {/* Account Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-stone-500 dark:text-stone-400 block">Account Status</span>
                <span className="text-stone-900 dark:text-white font-medium">{accountStatus || 'N/A'}</span>
              </div>
              <div>
                <span className="text-stone-500 dark:text-stone-400 block">Payment Status</span>
                <span className="text-stone-900 dark:text-white font-medium">{paymentStatus || 'N/A'}</span>
              </div>
              <div>
                <span className="text-stone-500 dark:text-stone-400 block">Balance</span>
                <span className="text-stone-900 dark:text-white font-medium">{formatCurrency(balance)}</span>
              </div>
              <div>
                <span className="text-stone-500 dark:text-stone-400 block">Past Due</span>
                <span className={`font-medium ${pastDue > 0 ? 'text-red-600 dark:text-red-400' : 'text-stone-900 dark:text-white'}`}>
                  {formatCurrency(pastDue)}
                </span>
              </div>
            </div>

            {/* Field Level Breakdown */}
            {fieldBreakdown.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">Field-Level Breakdown</h5>
                <div className="space-y-2">
                  {fieldBreakdown.slice(0, 5).map((field, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-white dark:bg-stone-800 rounded-lg p-2 border border-stone-200 dark:border-stone-700">
                      <span className="text-stone-600 dark:text-stone-400">{field.field_name || field.field}</span>
                      <div className="flex items-center gap-4">
                        {Object.entries(field.bureau_values || field.values || {}).map(([bureau, value]) => (
                          <span key={bureau} className="text-xs">
                            <span className="text-stone-500">{bureau}:</span>{' '}
                            <span className="text-stone-900 dark:text-white font-medium">{value || 'N/A'}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matched Rules */}
            {matchedRules.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2 flex items-center gap-2">
                  <BookOpen size={14} /> Matched Rules
                </h5>
                <div className="space-y-2">
                  {matchedRules.slice(0, 3).map((rule, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-white dark:bg-stone-800 rounded-lg p-2 border border-stone-200 dark:border-stone-700">
                      <div>
                        <span className="text-stone-900 dark:text-white font-medium">{rule.rule_name}</span>
                        {rule.match_reasons && rule.match_reasons.length > 0 && (
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                            {rule.match_reasons[0]}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewRule(rule.rule_key)}
                        className="text-emerald-600 dark:text-emerald-400"
                      >
                        <Eye size={14} className="mr-1" /> View Rule
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Red Flags */}
            {matchedRedFlags.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2 flex items-center gap-2">
                  <AlertTriangle size={14} /> Red Flags Detected
                </h5>
                <div className="space-y-2">
                  {matchedRedFlags.slice(0, 3).map((flag, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-red-50 dark:bg-red-900/20 rounded-lg p-2 border border-red-200 dark:border-red-800">
                      <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                      <span className="text-red-700 dark:text-red-300">{flag.red_flag_name}</span>
                      {flag.severity && (
                        <Badge variant="outline" className="ml-auto text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          {flag.severity}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Consumer Summary */}
            {account.consumer_friendly_summary && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                <p className="text-sm text-emerald-800 dark:text-emerald-300">{account.consumer_friendly_summary}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rule Summary Drawer */}
      <RuleSummaryDrawer
        ruleKey={selectedRuleKey}
        open={ruleDrawerOpen}
        onClose={() => setRuleDrawerOpen(false)}
      />
    </>
  );
};

export default Metro2AccountCard;
export { RuleSummaryDrawer, ELIGIBILITY_CONFIG, BUREAU_MATCH_STATUS, FIELD_LOGIC_STATUS };
